import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import Page from 'components/layouts/page/Page';
import Button from 'components/shared/Button';
import CircularProgress from 'components/shared/CircularProgress';
import Menu from 'components/shared/menu';
import Tabs, { TabType } from 'components/Tabs';
import useNow from 'hooks/useNow';
import useWorkflow from 'hooks/useWorkflow';
import { simpleDateAtTime } from 'lib/date';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall, BodySmallBold, H2 } from 'styles/typography';
import BackArrowIcon from 'svg/BackArrowIcon';
import EllipsisIcon from 'svg/EllipsisIcon';
import PencilIcon from 'svg/PencilIcon';

import EligibilityCheckSlideout from './eligibility/EligibilityCheckSlideout';
import ActivityLog from './ActivityLog';
import CategoryCard from './CategoryCard';
import { PORTAL_AUTOMATION_API_NAME, sourceSystemLabel, workflowBasePath } from './constants';
import { elapsed } from './inboundReferralsTableColumns';
import ProgressRail from './ProgressRail';
import ReferralModal, { ReferralModalSection } from './ReferralModal';
import ReferralSentTo from './ReferralSentTo';
import { WorkflowStatusBadge } from './StatusBadge';

// The page's standard tab strip (components/Tabs, same as the patient story page).
const PAGE_TABS: readonly TabType[] = [
  { label: 'Referral Review', value: 'review' },
  { label: 'Activity Log', value: 'activity' },
];

export default function InboundReferralPage() {
  const navigate = useNavigate();
  const { workflowId } = useParams<{ workflowId: string }>();
  const {
    workflow,
    isLoading,
    isError,
    completeActionAsync,
    saveActionProgress,
    isSavingActionProgress,
    isCompletingAction,
    amendActionAsync,
    isAmendingAction,
    invokeAction,
    isInvokingAction,
    markActionViewed,
  } = useWorkflow(workflowId);
  // null = closed; otherwise the modal opens in edit mode focused on this section.
  const [editingSection, setEditingSection] = useState<ReferralModalSection | null>(null);
  const [eligibilityOpen, setEligibilityOpen] = useState(false);
  const [tab, setTab] = useState<TabType>(PAGE_TABS[0]);
  // The subline's "- N min ago" ticks on its own; server timestamps stay the source of truth.
  const now = useNow(30_000);

  if (isLoading)
    return (
      <Centered>
        <CircularProgress />
      </Centered>
    );
  if (isError || !workflow)
    return (
      <Centered>
        <Body>We couldn’t load this referral.</Body>
      </Centered>
    );

  // The page renders any workflow type; back navigation returns to the type's own list.
  // The edit page (pills/packet) is referral-intake-specific, so hide it elsewhere.
  const isPortalAutomation = workflow.definition.apiName === PORTAL_AUTOMATION_API_NAME;
  // A portal run has no patient name until retrieval completes — title it by its source.
  const sourceSystem = workflow.valueFor('source_system')?.value;
  const portalFallback = sourceSystem ? `Portal referral — ${sourceSystemLabel(sourceSystem)}` : 'Portal referral';
  const patientName = isPortalAutomation && !workflow.hasPatientName ? portalFallback : workflow.patientName;

  return (
    <Page>
      {editingSection && (
        <ReferralModal workflow={workflow} section={editingSection} onClose={() => setEditingSection(null)} />
      )}
      <EligibilityCheckSlideout
        open={eligibilityOpen}
        onClose={() => setEligibilityOpen(false)}
        workflow={workflow}
        onComplete={(actionId, outputs) => completeActionAsync({ actionId, outputs }).catch(() => undefined)}
        isCompleting={isCompletingAction}
        onInvoke={(actionId, intent) => invokeAction({ actionId, invokeAction: intent })}
        isInvoking={isInvokingAction}
        onEditDetails={() => setEditingSection('demographics')}
      />
      <Page.Header>
        <HeaderInner>
          <TitleRow>
            <BackButton
              type='button'
              onClick={() => navigate(workflowBasePath(workflow.definition.apiName))}
              aria-label='Back'>
              <BackArrowIcon width={20} height={20} />
            </BackButton>
            <H2>{patientName}</H2>
            <WorkflowStatusBadge status={workflow.status} />
            {workflow.hasOutcome && <OutcomeChip data-testid='workflow-outcome'>{workflow.outcome}</OutcomeChip>}
            {!workflow.isTerminal && !isPortalAutomation && (
              <MenuWrap>
                <Menu $size='large'>
                  <Menu.Trigger>
                    <Button
                      variant='outline-gray'
                      style={{ width: 44, height: 44, padding: 0, flexShrink: 0 }}
                      data-testid='referral-actions'>
                      <EllipsisIcon />
                    </Button>
                  </Menu.Trigger>
                  <Menu.Content position='right' style={{ marginTop: 4 }}>
                    <EditMenuItem data-testid='edit-referral' onClick={() => setEditingSection('documents')}>
                      <PencilIcon color='transparent' stroke={colors.black100} strokeWidth={1.5} />
                      Edit
                    </EditMenuItem>
                  </Menu.Content>
                </Menu>
              </MenuWrap>
            )}
          </TitleRow>
          {workflow.createdAt && (
            <Subline>
              Created on {simpleDateAtTime(workflow.createdAt)}
              {workflow.createdByName && ` by ${workflow.createdByName}`} - {elapsed(workflow.createdAt, now)} ago
            </Subline>
          )}
        </HeaderInner>
        <TabRow>
          <Tabs tabs={PAGE_TABS} selectedTab={tab} onTabClick={setTab} />
        </TabRow>
      </Page.Header>
      <Page.Content>
        {tab.value === 'activity' && (
          <ActivityPane>
            <ActivityLog workflowId={workflow.id} isTerminal={workflow.isTerminal} />
          </ActivityPane>
        )}
        {/* Hidden rather than unmounted: the decision panels hold in-progress local
            state (selections, notes) that a peek at the Activity Log must not wipe. */}
        <Layout style={{ display: tab.value === 'review' ? undefined : 'none' }}>
          <RailColumn>
            {/* Keyed by run: the route param can change without a remount, and the
                modal's open/search state must not leak across referrals. */}
            <ReferralSentTo key={workflow.id} workflow={workflow} />
            <ProgressRail workflow={workflow} />
          </RailColumn>

          <MainColumn>
            {workflow.actionsByCategory().map(({ category, actions }) => (
              <CategoryCard
                key={category.key}
                category={category}
                actions={actions}
                workflow={workflow}
                onComplete={(actionId, outputs) => completeActionAsync({ actionId, outputs }).catch(() => undefined)}
                onAmend={(actionId, outputs) => amendActionAsync({ actionId, outputs }).catch(() => undefined)}
                isCompleting={isCompletingAction || isAmendingAction}
                onSaveProgress={(actionId, outputs) => saveActionProgress({ actionId, outputs })}
                isSavingProgress={isSavingActionProgress}
                onInvoke={(actionId, intent) => invokeAction({ actionId, invokeAction: intent })}
                isInvoking={isInvokingAction}
                onView={(actionId) => markActionViewed({ actionId })}
                onEdit={setEditingSection}
                onOpenEligibility={() => setEligibilityOpen(true)}
              />
            ))}
          </MainColumn>
        </Layout>
      </Page.Content>
    </Page>
  );
}

const Centered = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const HeaderInner = styled.div`
  padding: 24px 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const MenuWrap = styled.div`
  margin-left: auto;
`;

const EditMenuItem = styled(Menu.Item)`
  gap: 12px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${colors.black100};
`;

const Subline = styled(BodySmall)`
  color: ${colors.black50};
`;

const OutcomeChip = styled(BodySmallBold)`
  text-transform: capitalize;
  padding: 3px 12px;
  border-radius: 24px;
  background: ${colors.blue10};
  color: ${colors.blue100};
`;

// The tabs band under the header content, styled like PatientHeader's: a
// hairline above, and the header's own border-bottom closing beneath the
// active tab's underline.
const TabRow = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.black10};
  padding: 0 24px;
`;

const ActivityPane = styled.div`
  max-width: 760px;
`;

const Layout = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const RailColumn = styled.div`
  flex: 0 0 300px;
  max-width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 900px) {
    flex: 1 1 auto;
    max-width: none;
    width: 100%;
  }
`;

const MainColumn = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 720px;
`;
